import React from 'react';
import { Box, Typography, TextField } from '@mui/material';
import Dropdown, { DropdownOption } from '../../ui/dropdown';

interface CustomRecurrenceBuilderProps {
    recurrencePattern: string;
    setRecurrencePattern: (val: string) => void;
    startDate: string;
}

const CustomRecurrenceBuilder: React.FC<CustomRecurrenceBuilderProps> = ({
    recurrencePattern,
    setRecurrencePattern,
    startDate
}) => {
    // Parse the pattern: frequency-dayType-dayValue-interval
    const parsePattern = (pattern: string) => {
        const parts = pattern.split('-');
        return {
            frequency: parts[0] || 'monthly',
            dayType: parts[1] || 'day',
            dayValue: parts[2] || '1st',
            interval: parts[3] || '1'
        };
    };

    const parsed = parsePattern(recurrencePattern);

    const frequencyOptions: DropdownOption[] = [
        { value: 'monthly', label: 'Monthly' }
    ];

    const weekdayOptions: DropdownOption[] = [
        { value: 'Monday', label: 'Monday' },
        { value: 'Tuesday', label: 'Tuesday' },
        { value: 'Wednesday', label: 'Wednesday' },
        { value: 'Thursday', label: 'Thursday' },
        { value: 'Friday', label: 'Friday' },
        { value: 'Saturday', label: 'Saturday' },
        { value: 'Sunday', label: 'Sunday' }
    ];

    const monthlyDayOptions: DropdownOption[] = Array.from({ length: 31 }, (_, i) => ({
        value: String(i + 1),
        label: String(i + 1)
    }));

    const dayTypeOptions: DropdownOption[] = [
        { value: 'day', label: 'day' }
    ];

    const intervalOptions: DropdownOption[] = [
        { value: '1', label: '1' },
        { value: '2', label: '2' },
        { value: '3', label: '3' },
        { value: '4', label: '4' },
        { value: '5', label: '5' },
        { value: '6', label: '6' }
    ];

    const handleIntervalChange = (value: string) => {
        const numValue = value ? String(Math.max(1, parseInt(value) || 1)) : '1';
        buildPattern(parsed.frequency, parsed.dayType, parsed.dayValue, numValue);
    };

    const getUnitLabel = (freq: string): string => {
        const units: { [key: string]: string } = {
            'daily': 'day(s)',
            'weekly': 'week(s)',
            'monthly': 'month(s)',
            'yearly': 'year(s)'
        };
        return units[freq] || 'month(s)';
    };

    const buildPattern = (frequency: string, dayType: string, dayValue: string, interval: string) => {
        const pattern = `${frequency}-${dayType}-${dayValue}-${interval}`;
        setRecurrencePattern(pattern);
    };

    const getDayLabel = (freq: string): string => {
        switch (freq) {
            case 'weekly':
                return 'on';
            case 'monthly':
            case 'yearly':
                return 'on';
            default:
                return '';
        }
    };

    const getDayTypeLabel = (freq: string): string => {
        switch (freq) {
            case 'monthly':
            case 'yearly':
                return 'of the';
            default:
                return '';
        }
    };

    const shouldShowDay = (freq: string): boolean => {
        return freq !== 'daily';
    };

    const getDayOptions = (freq: string): DropdownOption[] => {
        switch (freq) {
            case 'weekly':
                return weekdayOptions;
            case 'monthly':
            case 'yearly':
                return monthlyDayOptions;
            default:
                return [];
        }
    };

    if (!startDate) {
        return (
            <Box sx={{ mb: 1.5, p: 1, border: '1px solid #bdbdbdff', borderRadius: 1, backgroundColor: 'var(--color-container-background)', minHeight: 40, display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ fontSize: 13, color: '#9e9e9e', fontStyle: 'italic' }}>
                    Select a start date first
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ mb: 1.5, display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
            <Dropdown
                options={frequencyOptions}
                value={parsed.frequency}
                onChange={(val) => buildPattern(val, parsed.dayType, parsed.dayValue, parsed.interval)}
                height={40}
                width={140}
            />
            {shouldShowDay(parsed.frequency) && (
                <>
                    <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)', fontWeight: 500 }}>
                        {getDayLabel(parsed.frequency)}
                    </Typography>
                    {(parsed.frequency === 'monthly' || parsed.frequency === 'yearly') && (
                        <>
                            <Dropdown
                                options={dayTypeOptions}
                                value={parsed.dayType}
                                onChange={(val) => buildPattern(parsed.frequency, val, parsed.dayValue, parsed.interval)}
                                height={40}
                                width={100}
                            />
                            <Dropdown
                                options={getDayOptions(parsed.frequency)}
                                value={parsed.dayValue}
                                onChange={(val) => buildPattern(parsed.frequency, parsed.dayType, val, parsed.interval)}
                                height={40}
                                width={100}
                            />
                        </>
                    )}
                    {parsed.frequency === 'weekly' && (
                        <Dropdown
                            options={getDayOptions(parsed.frequency)}
                            value={parsed.dayValue}
                            onChange={(val) => buildPattern(parsed.frequency, parsed.dayType, val, parsed.interval)}
                            height={40}
                            width={140}
                        />
                    )}
                </>
            )}
            <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)', fontWeight: 500 }}>of every</Typography>
            <TextField
                type="number"
                value={parsed.interval}
                onChange={(e) => handleIntervalChange(e.target.value)}
                inputProps={{ min: '1', style: { textAlign: 'center', fontSize: 13 } }}
                sx={{ width: 70, '& .MuiOutlinedInput-root': { height: 40 } }}
            />
            <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)', fontWeight: 500 }}>
                {getUnitLabel(parsed.frequency)}
            </Typography>
        </Box>
    );
};

export default CustomRecurrenceBuilder;
